const { GAME_CONFIG, GAME_STAGES } = require('./config');
const INTRO_PHRASE = [['left'], ['right'], ['left'], ['right'], ['left', 'right'], ['left'], ['right'], ['left', 'right']];

const createSeededRandom = (seed) => {
  let value = seed >>> 0;
  return () => {
    value = (value * 1664525 + 1013904223) >>> 0;
    return value / 0x100000000;
  };
};

const chooseOne = (rng, values) => values[Math.floor(rng() * values.length) % values.length];

const shouldSpawnOnRule = (tick, rule, rng) => tick % rule.interval === rule.offset && rng() < rule.chance;

const buildSpawnTimeline = (sixteenthInterval, seed = 0) => {
  const spawnTimeline = [];
  const maxTick = Math.ceil(GAME_CONFIG.time.maxDuration / sixteenthInterval);
  // Leave a final beat for releases and late judgements before the result.
  const chartEndTime = GAME_CONFIG.time.maxDuration - sixteenthInterval * 4;
  // Shared across stages so a hold near a stage boundary also protects the
  // start of the next one.
  const laneHoldUntilTick = { left: -1, right: -1 };
  const lastNoteTickByLane = { left: -Infinity, right: -Infinity };

  GAME_STAGES.forEach((stage) => {
    const stageStartTick = Math.ceil(stage.startTime / sixteenthInterval);
    const stageEndTick = Math.ceil(stage.endTime / sixteenthInterval);
    const rng = createSeededRandom(stage.spawn.seed + seed + Math.round(sixteenthInterval * 10000));
    const holdConfig = stage.spawn.hold;

    for (let tick = stageStartTick; tick < stageEndTick && tick <= maxTick; tick += 1) {
      if (tick * sixteenthInterval >= chartEndTime) break;
      let shouldSpawn = false;
      for (let index = 0; index < stage.spawn.rules.length; index += 1) {
        if (shouldSpawnOnRule(tick, stage.spawn.rules[index], rng)) {
          shouldSpawn = true;
          break;
        }
      }

      if (!shouldSpawn) continue;

      // A hold occupies its lane while it plays and needs a breath of gap
      // after it ends, otherwise the next note would demand an instant
      // release-and-repress. Skip notes in that lane until then.
      const lanes = (stage.id === 'intro'
        ? INTRO_PHRASE[Math.floor(tick / 8) % INTRO_PHRASE.length]
        : chooseOne(rng, stage.spawn.lanePatterns))
        .filter((lane) => laneHoldUntilTick[lane] <= tick);
      if (lanes.length === 0) continue;

      let holdDuration = 0;
      let holdTicks = 0;
      if (holdConfig && rng() < holdConfig.chance) {
        const candidateTicks = chooseOne(rng, holdConfig.ticks);
        const gapTicks = holdConfig.gapTicks ?? 4;
        // Only convert to a hold if every affected lane also had breathing
        // room before its head, so a hold never sits right on top of the
        // previous hit of that lane.
        const canHold = lanes.every((lane) => tick - lastNoteTickByLane[lane] >= gapTicks);
        if (canHold && (tick + candidateTicks) * sixteenthInterval < chartEndTime) {
          holdTicks = candidateTicks;
          holdDuration = holdTicks * sixteenthInterval;
          lanes.forEach((lane) => {
            laneHoldUntilTick[lane] = tick + holdTicks + gapTicks;
          });
        }
      }

      lanes.forEach((lane) => {
        lastNoteTickByLane[lane] = tick;
      });

      const targetHitTime = tick * sixteenthInterval;
      spawnTimeline.push({
        tick,
        stageId: stage.id,
        targetHitTime,
        lanes,
        holdTicks,
        holdDuration,
      });
    }
  });

  return spawnTimeline;
};


// Keep the procedural chart available for reference; live play and server
// verification use the identical recorded instrument attacks.
module.exports = { buildSpawnTimeline: require('./song').buildSongChart, buildLegacySpawnTimeline: buildSpawnTimeline };
